import { Sparkles } from "lucide-react";
import { ALG, PAT, SHX } from "@/constants/testIds";
import EncounterTable from "@/components/patient/EncounterTable";
import RecentEncountersActions from "@/components/patient/RecentEncountersActions";
import { NA } from "@/lib/patientChart";
import {
  ALLERGIES_EMPTY,
  ALLERGIES_NKA,
  MEDICAL_HISTORY_EMPTY,
  PROBLEM_LIST_EMPTY,
  SURGICAL_HISTORY_EMPTY,
  formatSummaryHistory,
  formatSummaryProblem,
} from "@/lib/patientSummary";

function Card({ title, action, children, testId, cardId, className = "" }) {
  return (
    <section
      data-testid={testId}
      data-summary-card-id={cardId}
      className={`emr-chart-card ${className}`.trim()}
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="emr-chart-card__title">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function ViewAll({ onClick, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="text-xs font-medium text-[#1E79E8] hover:text-[#1E79E8] hover:underline"
    >
      View All
    </button>
  );
}

function TextLink({ onClick, testId, children }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="emr-chart-link text-xs font-medium hover:underline"
    >
      {children}
    </button>
  );
}

function EmptyHint({ children }) {
  return <p className="emr-chart-empty">{children}</p>;
}

function Field({ label, children, testId }) {
  return (
    <div className="emr-chart-field">
      <dt className="shrink-0">{label}</dt>
      <dd data-testid={testId}>{children ?? NA}</dd>
    </div>
  );
}

function DiagnosisRows({ items, testIdPrefix }) {
  return (
    <ul className="space-y-2 text-sm">
      {items.map((item, index) => {
        const row = formatSummaryProblem(item);
        const key = row.code || item.id || `${row.description}-${index}`;
        return (
          <li
            key={key}
            data-testid={`${testIdPrefix}-${row.code || index}`}
            className="flex items-start justify-between gap-3"
          >
            {row.code ? (
              <span className="shrink-0 font-fhir text-xs text-slate-500">{row.code}</span>
            ) : null}
            <span className="min-w-0 flex-1 text-slate-800">{row.description}</span>
          </li>
        );
      })}
    </ul>
  );
}

export function ProblemListCard({ items = [], onViewAll }) {
  return (
    <Card
      title="Problem List"
      cardId="problem-list"
      testId={PAT.chartCardProblemList}
      className="p-4"
      action={<ViewAll onClick={onViewAll} testId={PAT.chartViewAllProblems} />}
    >
      {items.length ? (
        <DiagnosisRows items={items} testIdPrefix={PAT.chartProblemRow} />
      ) : (
        <p className="text-sm text-slate-400">{PROBLEM_LIST_EMPTY}</p>
      )}
    </Card>
  );
}

export function MedicalHistoryCard({ conditions = [], negatives = [], onViewAll }) {
  const empty = conditions.length === 0 && negatives.length === 0;
  return (
    <Card
      title="Medical History"
      cardId="medical-history"
      testId={PAT.chartCardMedicalHistory}
      className="p-4"
      action={<ViewAll onClick={onViewAll} testId={PAT.chartViewAllHistory} />}
    >
      {empty ? (
        <p className="text-sm text-slate-400">{MEDICAL_HISTORY_EMPTY}</p>
      ) : (
        <div className="space-y-3">
          {conditions.length ? (
            <DiagnosisRows
              items={conditions.map((item) => formatSummaryHistory(item))}
              testIdPrefix={PAT.chartHistoryRow}
            />
          ) : null}
          {negatives.length ? (
            <div>
              <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Pertinent Negatives
              </h3>
              <ul className="space-y-1 text-sm text-slate-700">
                {negatives.map((item, index) => (
                  <li
                    key={item.id || item.description || index}
                    data-testid={`${PAT.chartHistoryNegative}-${index}`}
                  >
                    {item.description}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      )}
    </Card>
  );
}

export function AllergiesCard({ items = [], noKnownAllergies = false, onViewAll }) {
  return (
    <Card
      title="Allergies"
      cardId="allergies"
      testId={ALG.card}
      className="p-4"
      action={<ViewAll onClick={onViewAll} testId={ALG.viewAll} />}
    >
      {items.length ? (
        <ul className="space-y-2 text-sm">
          {items.map((item, index) => (
            <li
              key={item.id || item.allergenId || index}
              className="flex items-start justify-between gap-3"
            >
              <span className="min-w-0 flex-1 text-slate-800">{item.allergenDisplayName}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-slate-400">{noKnownAllergies ? ALLERGIES_NKA : ALLERGIES_EMPTY}</p>
      )}
    </Card>
  );
}

export function SurgicalHistoryCard({ items = [], onViewAll }) {
  return (
    <Card
      title="Surgical History"
      cardId="surgical-history"
      testId={SHX.card}
      className="p-4"
      action={<ViewAll onClick={onViewAll} testId={SHX.viewAll} />}
    >
      {items.length ? (
        <ul className="space-y-2 text-sm">
          {items.map((item, index) => (
            <li
              key={item.id || item.localCode || index}
              className="flex items-start justify-between gap-3"
            >
              <span className="shrink-0 font-fhir text-xs text-slate-500">{item.localCode}</span>
              <span className="min-w-0 flex-1 text-slate-800">{item.display}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-slate-400">{SURGICAL_HISTORY_EMPTY}</p>
      )}
    </Card>
  );
}

export function MedicationsSummaryCard() {
  return (
    <Card title="Medications (Active)" cardId="medications" testId={PAT.chartComingSoonMedications}>
      <EmptyHint>No patient-specific medications are available yet.</EmptyHint>
      <p data-testid={PAT.chartFactMedications} className="sr-only">
        {NA}
      </p>
    </Card>
  );
}

export function RecentEncountersCard({ encounters = [], onNewEncounter, onViewAll, onSelectEncounter }) {
  return (
    <Card
      title="Recent Encounters"
      cardId="recent-encounters"
      testId={PAT.chartCardEncounters}
      action={<RecentEncountersActions onNewEncounter={onNewEncounter} onViewAll={onViewAll} />}
    >
      <EncounterTable encounters={encounters} compact onSelect={onSelectEncounter} />
    </Card>
  );
}

export function OrdersResultsSummaryCard({ onViewAll }) {
  return (
    <Card
      title="Recent Orders / Results"
      cardId="orders-results"
      testId={PAT.chartCardOrdersResults}
      action={onViewAll ? <TextLink onClick={onViewAll}>View all</TextLink> : null}
    >
      <EmptyHint>Diagnostic results are not stored on the current patient record.</EmptyHint>
    </Card>
  );
}

export function HealthMaintenanceSummaryCard() {
  return (
    <Card title="Health Maintenance" cardId="health-maintenance" testId={PAT.chartCardHealthMaintenance}>
      <EmptyHint>Health-maintenance items are not stored on the current patient record.</EmptyHint>
    </Card>
  );
}

export function VitalSignsSummaryCard() {
  return (
    <Card title="Vital Signs (Most Recent)" cardId="vital-signs" testId={PAT.chartCardVitalSigns}>
      <EmptyHint>Vital signs are not stored on the current patient record.</EmptyHint>
    </Card>
  );
}

export function PatientSnapshotCard({
  fields = {},
  encounters = [],
  problemList = [],
  showProblems = false,
  onOpenFrontDesk,
}) {
  return (
    <Card title="Patient Snapshot" cardId="patient-snapshot" testId={PAT.chartCardFacts}>
      <dl className="emr-chart-dl">
        <Field label="Age">{fields.age != null ? `${fields.age} y/o` : NA}</Field>
        <Field label="Gender">{fields.gender}</Field>
        <Field label="Language">{fields.language}</Field>
        <Field label="Encounters" testId={PAT.chartFactEncounters}>
          {encounters.length}
        </Field>
        {showProblems ? (
          <Field label="Problems" testId={PAT.chartFactDiagnoses}>
            {problemList.length}
          </Field>
        ) : null}
      </dl>
      {onOpenFrontDesk ? (
        <div className="mt-2">
          <TextLink onClick={onOpenFrontDesk} testId={PAT.chartViewAllDemographics}>
            View demographics
          </TextLink>
        </div>
      ) : null}
    </Card>
  );
}

export function AiPatientSummaryCard() {
  return (
    <section
      data-testid={PAT.chartAiPanel}
      data-summary-card-id="ai-patient-summary"
      className="emr-chart-card emr-chart-ai"
    >
      <h2 className="emr-chart-card__title flex items-center gap-2">
        <Sparkles className="h-4 w-4" />
        AI Patient Summary
      </h2>
      <p className="emr-chart-ai__note">
        This panel is reserved for a future AI Patient Summary. It does not analyze patient data
        and does not call an AI service.
      </p>
      <div className="emr-chart-ai__block">
        <h3>Overview</h3>
        <p>Not generated.</p>
      </div>
      <div className="emr-chart-ai__block">
        <h3>Current Issues</h3>
        <p>Not generated.</p>
      </div>
      <div className="emr-chart-ai__block">
        <h3>Medications</h3>
        <p>Not generated.</p>
      </div>
      <div className="emr-chart-ai__block">
        <h3>Allergies</h3>
        <p>Not generated.</p>
      </div>
      <div className="emr-chart-ai__block">
        <h3>Recent Results</h3>
        <p>Not generated.</p>
      </div>
      <div className="emr-chart-ai__block">
        <h3>Plan</h3>
        <p>Not generated.</p>
      </div>
      <button type="button" disabled className="emr-chart-ai__btn">
        <Sparkles className="h-4 w-4" />
        Generate Summary
      </button>
    </section>
  );
}
